import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { SiteListItem, type SitesQueryParams, SitesResponse } from './types';
import {
  zodSiteListItemSchema,
  zodSitesQueryParamsSchema,
  zodSitesResponseSchema,
} from './validations';

/**
 * Fetches all sites from the API.
 *
 * @param queryParams - The query parameters for fetching sites.
 * @param options - Optional ky request options including abort signal.
 * @returns The sites list data with pagination information.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/get_sites_site_get
 */
export const fetchSites = async (
  queryParams: Partial<SitesQueryParams>,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.SITE.GET_ALL(), {
      searchParams: queryParams,
      validate: {
        searchParams: zodSitesQueryParamsSchema,
      },
      ...options,
    })
    .json<SitesResponse>();

  // Validate the returned data
  const validatedResponse = zodSitesResponseSchema.safeParse(response);

  if (validatedResponse.success) {
    return validatedResponse.data;
  }

  // Some data is invalid - remove all invalid sites from the response and return the rest
  const filteredResponse = {
    ...response,
    data: response.data.filter(
      (site: SiteListItem) => zodSiteListItemSchema.safeParse(site).success,
    ),
  };

  return filteredResponse;
};
